import CloseRounded from '@mui/icons-material/CloseRounded';
import { Box, Button, Card, Modal, Typography } from '@mui/material';
import CartContext from 'contexts/CartContext';
import useBoolean from 'hooks/useBoolean';
import ChevronLeft from 'icons/ChevronLeft';
import ChevronRight from 'icons/ChevronRight';
import DashboardLayout from 'layouts/Dashboard/DashboardLayout';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axios';
import SummaryTable from './partials/SummaryTable';
import WorkshopCard from './partials/WorkshopCard';
import useAuth from 'hooks/useAuth';

const ChooseWorkshop = () => {
    const [dataWorkshop, setDataWorkshop] = useState([]);
    const [messageError, setMessageError] = useState();

    const cart = useContext(CartContext);
    const openModal = useBoolean();
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        const getData = async () => {
            let shouldStop = false;
            await axiosInstance
                .get('/api/prv/member-invoices', {
                    params: {
                        packageType: 'main',
                    },
                })
                .then((res) => {
                    const data = res?.data || [];
                    const pendingInvoice = data.find(
                        (item) => 'pending' === item.status.value,
                    );
                    const paidInvoice = data.find(
                        (item) => 'paid' === item.status.value,
                    );
                    const found = pendingInvoice || paidInvoice;
                    if (found) {
                        cart.setFromInvoice(found);
                        navigate('/dashboard/payment-complete');
                        shouldStop = true;
                    }
                })
                .catch((err) => console.log(err));

            if (shouldStop) return;

            if (!cart.package) {
                navigate('/dashboard/choose-package');
                return;
            }

            await axiosInstance
                .get('/api/prv/workshops')
                .then((res) => {
                    setDataWorkshop(res.data);
                })
                .catch((err) => console.log(err));
        };
        getData();
    }, [navigate, cart.package]);

    const isConflict = (data) => {
        if (alreadyChoose(data)) return false;

        return cart.workshops.some((workshop) => {
            return workshop.timeRange.some((slot) => {
                return data.timeRange.some((slot2) => {
                    return slot === slot2;
                });
            });
        });
    };

    const alreadyChoose = (data) => {
        return cart.workshops.some((workshop) => workshop.id === data.id);
    };

    const isBundlePackage = cart?.package?.isBundle;
    const includedFull = parseInt(cart?.package?.wsFullDayIncludes ?? 0) || 0;
    const includedHalf = parseInt(cart?.package?.wsHalfDayIncludes ?? 0) || 0;
    const selectedFullCount = cart.workshops.reduce(
        (acc, cur) => acc + (cur?.workshopType?.value === 'full_day' ? 1 : 0),
        0,
    );
    const selectedHalfCount = cart.workshops.reduce(
        (acc, cur) => acc + (cur?.workshopType?.value === 'half_day' ? 1 : 0),
        0,
    );

    const isNextDisabled = () => {
        if (cart.workshops.length === 0) return true;
        if (!isBundlePackage) return false;
        if (includedFull > 0 && selectedFullCount < includedFull) return true;
        if (includedHalf > 0 && selectedHalfCount < includedHalf) return true;
        return false;
    };

    const isValidToAdd = (data) => {
        if (isConflict(data)) {
            openModal.onTrue();
            setMessageError(
                'The workshop you selected conflicts with other workshops already chosen',
            );

            return false;
        }

        return true;
    };

    const chooseWorkshop = async (data) => {
        if (!isValidToAdd(data)) return;

        handleChooseWorkshop(data);
    };

    const handleChooseWorkshop = (data) => {
        alreadyChoose(data)
            ? cart.removeWorkshop(data)
            : cart.addWorkshop(data);
    };

    const setInvoiceAmount = () => {
        const { package: plan, workshops } = cart;
        const wsHalfDayIncludes = parseInt(plan?.wsHalfDayIncludes ?? 0) || 0;
        const wsFullDayIncludes = parseInt(plan?.wsFullDayIncludes ?? 0) || 0;
        const hasWorkshopDiscountSelected =
            (workshops || []).some((ws) => ws?.isDiscount) || false;
        const isBundle = !!plan?.isBundle;
        const isNurse = user?.profession?.name === 'Nurse';

        const sortedByPrice = [...(workshops || [])].sort((a, b) =>
            b.price.localeCompare(a.price),
        );
        const selectedHalfDayWorkshops =
            sortedByPrice.find(
                (ws) => ws?.workshopType?.value === 'half_day',
            ) || {};
        const selectedFullDayWorkshops =
            sortedByPrice.find(
                (ws) => ws?.workshopType?.value === 'full_day',
            ) || {};

        const totalWorkshopPrice =
            workshops?.reduce(
                (acc, cur) => acc + parseInt(cur?.price || 0),
                0,
            ) || 0;

        const isCompleteHalfday =
            wsHalfDayIncludes === 0 ||
            (wsHalfDayIncludes > 0 &&
                Object.keys(selectedHalfDayWorkshops).length > 0);
        const isCompleteFullday =
            wsFullDayIncludes === 0 ||
            (wsFullDayIncludes > 0 &&
                Object.keys(selectedFullDayWorkshops).length > 0);

        const workshopBundlePrice =
            parseInt(selectedHalfDayWorkshops?.price || 0) * wsHalfDayIncludes +
            parseInt(selectedFullDayWorkshops?.price || 0) * wsFullDayIncludes;
        const totalPrice = plan.sympoPrice + workshopBundlePrice;
        let bundleDiscount =
            isCompleteHalfday && isCompleteFullday
                ? totalPrice - parseInt(plan?.price || 0)
                : 0;

        cart.setTotalWorkshopPrice(totalWorkshopPrice);
        if (isBundle && hasWorkshopDiscountSelected && isNurse) {
            bundleDiscount = plan.sympoPrice;
        } 
        
        cart.setBundleDiscount(bundleDiscount);

        const sympoDiscount =
            user?.hasDiscount &&
            !(isBundle && hasWorkshopDiscountSelected && isNurse)
                ? Math.floor(plan.sympoPrice * 0.3)
                : 0;
        cart.setTotal(
            plan.sympoPrice + totalWorkshopPrice - bundleDiscount - sympoDiscount,
        );
    };

    useEffect(() => {
        setInvoiceAmount();
    }, [cart.workshops, cart.package]);

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '85%', sm: 420 },
        bgcolor: 'background.paper',
        borderRadius: 3,
        boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
        p: 4,
        textAlign: 'center',
    };

    const handleNext = async () => {
        navigate('/dashboard/payment');
    };
    return (
        <DashboardLayout>
            <Modal
                open={openModal.value}
                onClose={() => openModal.onFalse()}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Box
                        sx={{
                            width: 84,
                            height: 84,
                            borderRadius: '50%',
                            bgcolor: 'error.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                            boxShadow: '0 12px 24px rgba(255,0,0,0.35)',
                        }}
                    >
                        <CloseRounded
                            sx={{ fontSize: 56, color: 'common.white' }}
                        />
                    </Box>
                    <Typography
                        id="modal-modal-title"
                        variant="h5"
                        fontWeight={800}
                    >
                        Failed to Add Workshop
                    </Typography>
                    <Typography
                        id="modal-modal-description"
                        sx={{ mt: 1.5 }}
                        color="text.secondary"
                    >
                        {messageError ??
                            'We were unable to process your request. Please check your selections and try again.'}
                    </Typography>
                    <Button
                        onClick={() => openModal.onFalse()}
                        variant="contained"
                        sx={{
                            mt: 3,
                            borderRadius: 2,
                            px: 4,
                            backgroundColor: 'grey.300',
                            color: 'text.primary',
                            '&:hover': { backgroundColor: 'grey.400' },
                        }}
                    >
                        CLOSE
                    </Button>
                </Box>
            </Modal>

            <Box pt={2} pb={4}>
                <Card sx={{ p: 4 }}>
                    <Box
                        mb={2}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Button
                            disableRipple
                            size="small"
                            variant="contained"
                            pr={3}
                            startIcon={<ChevronLeft />}
                            onClick={() =>
                                navigate('/dashboard/choose-package')
                            }
                        >
                            Back
                        </Button>
                        <Typography variant="h5" fontWeight={600}>
                            Choose Workshop
                        </Typography>
                        <Button
                            disableRipple
                            variant="contained"
                            size=""
                            endIcon={<ChevronRight />}
                            onClick={() => handleNext()}
                            disabled={isNextDisabled()}
                        >
                            Next
                        </Button>
                    </Box>

                    <Box sx={{ my: 4 }}>
                        <Typography
                            sx={{ fontSize: '24px', fontWeight: 600 }}
                            gutterBottom
                        >
                            {cart.package?.name}
                        </Typography>
                        <SummaryTable cart={cart} />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                            justifyContent: 'center',
                            mb: 4,
                        }}
                    >
                        {dataWorkshop
                            ?.sort((a, b) => a.order - b.order)
                            .map((item, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        flex: '1 1 340px',
                                        maxWidth: 360,
                                    }}
                                >
                                    <WorkshopCard
                                        item={item}
                                        selected={cart.workshops.some(
                                            (workshop) =>
                                                workshop.id === item.id,
                                        )}
                                        isSold={item?.sold}
                                        chooseWorkshop={() =>
                                            chooseWorkshop(item)
                                        }
                                    />
                                </Box>
                            ))}
                    </Box>
                </Card>
            </Box>
        </DashboardLayout>
    );
};

export default ChooseWorkshop;
